import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Minimize2,
  Maximize2,
  Phone,
  Mail,
  Clock,
  HelpCircle,
  DollarSign,
  Building2,
  TrendingUp,
  Shield
} from 'lucide-react';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  type?: 'text' | 'quick_reply' | 'suggestion';
}

interface QuickReply {
  id: string;
  text: string;
  action: string;
}

const Chatbot: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! I'm your Diasporawise investment assistant. How can I help you today?",
      isUser: false,
      timestamp: new Date(),
      type: 'text'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Quick reply options
  const quickReplies: QuickReply[] = [
    { id: '1', text: 'Investment Opportunities', action: 'investment' },
    { id: '2', text: 'Account Help', action: 'account' },
    { id: '3', text: 'Contact Support', action: 'contact' },
    { id: '4', text: 'Platform Features', action: 'features' }
  ];

  // Predefined responses
  const responses: { [key: string]: string } = {
    investment: "We offer various investment opportunities including real estate, technology startups, and renewable energy projects. You can browse all available projects on our Projects page. Would you like to know more about any specific type of investment?",
    account: "I can help you with account-related questions. You can create an account, reset your password, or update your profile information. What specific account help do you need?",
    contact: "You can reach our support team through:\n• Email: info@diasporawise.com\n• Live chat: Available 24/7\n• Help center: Visit our FAQ section",
    features: "Our platform offers:\n• Real-time investment tracking\n• Secure payment processing\n• Portfolio management\n• Market analysis\n• Mobile app access\n• 24/7 customer support",
    default: "I understand you're looking for help. Let me connect you with our support team or you can try asking about:\n• Investment opportunities\n• Account management\n• Platform features\n• Contact information"
  };

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isMinimized]);

  // Don't show chatbot to admins or super admins
  if (user && ['admin', 'manager', 'exclusive'].includes(user.role)) {
    return null;
  }

  const handleSendMessage = async (text: string) => {
    if (!text.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: text.trim(),
      isUser: true,
      timestamp: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Keep input focused after sending message
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    // Simulate bot response delay
    setTimeout(() => {
      const botResponse = generateBotResponse(text);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botResponse,
        isUser: false,
        timestamp: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);

      // Keep input focused after bot response
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }, 1000 + Math.random() * 1000);
  };

  const generateBotResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();

    if (input.includes('investment') || input.includes('invest') || input.includes('project')) {
      return responses.investment;
    } else if (input.includes('account') || input.includes('login') || input.includes('password')) {
      return responses.account;
    } else if (input.includes('contact') || input.includes('support') || input.includes('help')) {
      return responses.contact;
    } else if (input.includes('feature') || input.includes('platform') || input.includes('app')) {
      return responses.features;
    } else if (input.includes('hello') || input.includes('hi') || input.includes('hey')) {
      return "Hello! Welcome to Diasporawise. I'm here to help you with any questions about our investment platform. How can I assist you today?";
    } else if (input.includes('price') || input.includes('cost') || input.includes('fee')) {
      return "Our platform fees are transparent and competitive. We charge a small percentage on successful investments. For detailed pricing information, please visit our pricing page or contact our support team.";
    } else if (input.includes('security') || input.includes('safe') || input.includes('secure')) {
      return "Security is our top priority. We use bank-grade encryption, two-factor authentication, and comply with all financial regulations. Your investments and personal data are fully protected.";
    } else if (input.includes('roi') || input.includes('return') || input.includes('profit')) {
      return "Our investment opportunities typically offer returns ranging from 12% to 25% annually, depending on the project type and risk level. All returns are clearly stated in each project's details.";
    } else if (input.includes('minimum') || input.includes('min') || input.includes('start')) {
      return "The minimum investment amount varies by project, typically starting from $1,000. Each project clearly displays its minimum investment requirement. You can start with any amount above the minimum.";
    } else if (input.includes('mobile') || input.includes('app') || input.includes('download')) {
      return "Yes! We have mobile apps available for both iOS and Android. You can download them from the App Store or Google Play Store. The mobile app provides full access to all platform features.";
    } else if (input.includes('withdraw') || input.includes('cash out') || input.includes('exit')) {
      return "Withdrawal policies vary by project type. Most investments have a lock-in period, after which you can withdraw your funds. Please check the specific terms for each investment opportunity.";
    } else {
      return responses.default;
    }
  };

  const handleQuickReply = (action: string) => {
    const replyText = quickReplies.find(r => r.action === action)?.text || action;
    handleSendMessage(replyText);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Chat Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white rounded-2xl shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 z-50 flex items-center justify-center group backdrop-blur-sm border border-blue-500/20"
          aria-label="Open chat"
        >
          <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform duration-300" />
          <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
            <span className="text-xs text-white font-bold">1</span>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed bottom-4 right-4 sm:bottom-5 sm:right-5 bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-200/50 z-50 transition-all duration-300 ${isMinimized
          ? 'w-72 sm:w-80 h-14 sm:h-16'
          : 'w-[calc(100vw-2rem)] sm:w-[420px] h-[calc(100vh-2rem)] sm:h-[560px] max-w-md flex flex-col'
          }`}>
          {/* Chat Header */}
          <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white p-3 sm:p-4 rounded-t-2xl sm:rounded-t-3xl flex items-center justify-between border-b border-blue-500/20">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                <Bot className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div>
                <h3 className="font-bold text-xs sm:text-sm">Diasporawise Assistant</h3>
                <p className="text-xs text-blue-100 flex items-center gap-1">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse"></div>
                  Online now
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 sm:p-2 hover:bg-white/20 rounded-xl transition-all duration-200 hover:scale-105"
                aria-label={isMinimized ? "Maximize chat" : "Minimize chat"}
              >
                {isMinimized ? <Maximize2 className="w-3 h-3 sm:w-4 sm:h-4" /> : <Minimize2 className="w-3 h-3 sm:w-4 sm:h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 sm:p-2 hover:bg-white/20 rounded-xl transition-all duration-200 hover:scale-105"
                aria-label="Close chat"
              >
                <X className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages Container */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 sm:space-y-3 h-[calc(100vh-12rem)] sm:h-[420px]">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex gap-2 sm:gap-3 max-w-[90%] sm:max-w-[85%] ${message.isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${message.isUser
                        ? 'bg-gradient-to-br from-green-500 to-green-600'
                        : 'bg-gradient-to-br from-blue-500 to-blue-600'
                        }`}>
                        {message.isUser ? <User className="w-3 h-3 sm:w-4 sm:h-4 text-white" /> : <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-white" />}
                      </div>
                      <div className={`rounded-xl sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-3 shadow-sm ${message.isUser
                        ? 'bg-gradient-to-br from-green-500 to-green-600 text-white'
                        : 'bg-gray-50 text-gray-800 border border-gray-100'
                        }`}>
                        <p className="text-xs sm:text-sm whitespace-pre-wrap leading-relaxed">{message.text}</p>
                        <p className={`text-xs mt-1 sm:mt-2 ${message.isUser ? 'text-green-100' : 'text-gray-500'
                          }`}>
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="flex gap-2 sm:gap-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                        <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                      </div>
                      <div className="bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-3 shadow-sm">
                        <div className="flex gap-1">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Replies */}
                {messages.length === 1 && (
                  <div className="space-y-2 sm:space-y-3">
                    <p className="text-xs text-gray-600 text-center font-semibold">Quick replies:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {quickReplies.map((reply) => (
                        <button
                          key={reply.id}
                          onClick={() => handleQuickReply(reply.action)}
                          className="p-2 sm:p-3 text-xs bg-white text-gray-800 rounded-lg sm:rounded-xl hover:bg-blue-600 hover:text-white transition-all duration-200 text-left border border-gray-200 hover:border-blue-600 hover:shadow-lg font-medium"
                        >
                          {reply.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-3 sm:p-4 border-t border-gray-200/50 bg-gray-50/50 rounded-b-2xl sm:rounded-b-3xl">
                <div className="flex gap-2 items-end">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="flex-1 px-3 py-2 sm:py-2.5 border border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-xs sm:text-sm bg-white shadow-sm transition-all duration-200 min-w-0"
                    disabled={isTyping}
                  />
                  <button
                    onClick={() => handleSendMessage(inputValue)}
                    disabled={!inputValue.trim() || isTyping}
                    className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-lg sm:rounded-xl flex items-center justify-center hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105 flex-shrink-0"
                    aria-label="Send message"
                  >
                    <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1 sm:mt-2 text-center">
                  Press Enter to send
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;
